import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as targetDomainSelectors from 'data/targetDomain/targetDomain.selectors';
import * as targetDomainActions from 'data/targetDomain/targetDomain.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { TargetDomain } from 'data/targetDomain/targetDomain.types';
import TargetDomainForm from 'routes/TargetDomain/components/TargetDomainForm';
import * as types from 'data/targetDomain/targetDomain.types';
import { targetDomainTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
  adaptFormData,
  adaptTargetDomainFormData,
  adaptTargetDomainTemplate,
} from '../../utils/targetDomainTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.TargetDomain
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  targetDomainTemplateDef: adaptTemplateVisibilityMode(targetDomainTemplate, 'view'),
  deliveryFunctionId: state.global.selectedDF,
  selectedTargetDomain: targetDomainSelectors
    .getSelectedTargetDomain(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedTargetDomain: targetDomainSelectors
    .getSelectedTargetDomain(state)[0],
  targetDomain: targetDomainSelectors.getTargetDomain(state),
});

const mapDispatch = {
  goToTargetDomainList: targetDomainActions.goToTargetDomainList,
  goToTargetDomainEdit: targetDomainActions.goToTargetDomainEdit,
  getTargetDomain: targetDomainActions.getTargetDomain,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const TargetDomainView = ({
  history,
  selectedTargetDomain,
  deliveryFunctionId,
  targetDomainTemplateDef,
  targetDomain,
  goToTargetDomainList,
  getTargetDomain,
  goToTargetDomainEdit,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<TargetDomain>>({});
  const [template, setTemplate] = useState(targetDomainTemplateDef);
  const { t } = useTranslation();

  const id = selectedTargetDomain?.id;

  useEffect(() => {
    if (id) {
      getTargetDomain(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getTargetDomain]);

  useEffect(() => {
    if (targetDomain != null && template != null) {
      let clonedTargetDomain = cloneDeep(targetDomain);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      clonedTargetDomain = adaptTargetDomainFormData(clonedTargetDomain);

      setTemplate(adaptTemplateOptions(clonedTemplate, targetDomain));
      setFormData(clonedTargetDomain);
    }
  }, [targetDomain, id]);

  const handleEdit = () => goToTargetDomainEdit(history);

  const handleCancel = () => goToTargetDomainList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as TargetDomain);
    let clonedTemplate = cloneDeep(template);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptTargetDomainTemplate(clonedTemplate);

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <TargetDomainForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing={false}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (targetDomain == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="TargetDomainView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('targetDomain.view.title'))}
          <div className="read-only">
            {renderForm()}
          </div>
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(TargetDomainView));
